import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import GraphPeriod from 'components/GraphPeriod'
import NoPerformance from 'components/NoPerformance'
import PerformanceGraph, { GraphPeriodType } from 'components/PerformanceGraph'
import { tradePath, myVaultPath } from 'components/Routes'
import { Product } from 'api/inventory'
import useSpotPrice from 'hooks/useSpotPrice'
import { useProductPerformance } from 'hooks/useInventory'
import { useMutateCreateReviewOrder } from 'hooks/useTrading'
import { memo, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDateToString, formattedPrice } from 'utils/util'
import { formatWeightAsGrams } from 'utils/formatWeightAsGrams'
import GridValue from './GridValue'
import TableTransactionHistory from './TableTransactionHistory'
const drawerBleeding = 80

interface MyVaultProductProps {
  product?: Product
  onClose: () => void
  vaultId?: number
}

function MyVaultProduct({
  product,
  onClose,
  vaultId
}: Readonly<MyVaultProductProps>): JSX.Element {
  const { user, setToast } = useAuth()
  const productId = product?.id

  const navigate = useNavigate()

  const [graphPeriod, setGraphPeriod] = useState<GraphPeriodType>('1w')

  const [, { data: spotPriceRaw }] = useSpotPrice({
    productId: productId ? Number(productId) : 0
  })
  const latestSpotPrice = spotPriceRaw

  const { data: productPerformanceData } = useProductPerformance(
    productId ? Number(productId) : 0,
    vaultId
  )

  const { mutateAsync: createReviewOrder } = useMutateCreateReviewOrder()

  const getUpdatedPriceTime = useCallback(() => {
    // Raw string, not new Date(): the API sends offsetless UTC and only
    // formatDateToString knows to treat it as such [BUG-032].
    return formatDateToString(latestSpotPrice?.spotTime ?? '')
  }, [latestSpotPrice])

  const isShowPerformanceGraph = useCallback(() => {
    const activatedVault = user?.activatedVault
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const hasHistory = user?.securitiesOwned?.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (s: any) => s?.product?.id === product?.id
    )?.hasHistory as boolean
    return activatedVault
      ? Boolean(
          activatedVault?.securitiesOwned?.find(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (s: any) => s.product.id === product?.id
          )
        )
      : hasHistory
  }, [product?.id, user?.activatedVault, user?.securitiesOwned])

  const getMidpointPerUnit = useCallback(() => {
    const midpointPerMg = latestSpotPrice?.midpointPerMg
    return midpointPerMg ? Number(midpointPerMg).toFixed(4) : '0.0000'
  }, [latestSpotPrice])

  const getCurrentValue = useCallback(() => {
    const currentValue = productPerformanceData?.currentValue

    const amount = Number(currentValue).toFixed(2)
    return amount ? formattedPrice(amount) : '0.00'
  }, [productPerformanceData])

  const getQuantityOwned = useCallback(() => {
    const quantityOwned = productPerformanceData?.quantityOwned

    return quantityOwned ? Number(quantityOwned) : 0
  }, [productPerformanceData])

  const { formattedDate, formattedHours, formattedMinutes, meridiem } =
    getUpdatedPriceTime() ?? {}

  const handleClickBuyMore = useCallback(() => {
    navigate(tradePath({ routeParams: { productId } }))
  }, [navigate, productId])

  const handleClickSell = useCallback(async () => {
    try {
      const reviewOrder = await createReviewOrder({
        productId: Number(productId),
        orderType: 'sell',
        quantityMg: 100
      })
      const { id } = reviewOrder ?? {}
      if (id) {
        navigate(
          myVaultPath({
            queryParams: { type: 'sell', id }
          })
        )
      }
    } catch (error) {
      setToast({
        open: true,
        message: error instanceof Error ? error.message : 'An error occurred',
        type: 'error',
        duration: 3000
      })
    }
  }, [navigate, productId, createReviewOrder, setToast])

  return (
    <Box>
      <Box textAlign='right'>
        <IconButton aria-label='Close drawer' edge='start' onClick={onClose}>
          <CloseIcon fontSize='large' />
        </IconButton>
      </Box>
      <Stack height={drawerBleeding} id='bullion'>
        <Box paddingX={2} display='flex' justifyContent='space-between'>
          <Box>
            <Typography color='primary' fontSize='1rem'>
              {product?.name.toUpperCase()} BULLION
            </Typography>
            <Typography color='primary' fontSize='1rem'>
              99.99% FINE {product?.name.toUpperCase()}
            </Typography>
          </Box>
          <Box textAlign='right'>
            <Typography color='primary' fontSize='0.75rem'>
              PER GRAM: ${(Number(getMidpointPerUnit()) * 1000).toFixed(2)}
            </Typography>
            <Typography color='primary' fontSize='0.75rem'>
              YOU OWN: ${getCurrentValue()}
            </Typography>
          </Box>
        </Box>
      </Stack>

      <Stack px={2} pb={4}>
        <Typography color='common.black' fontSize='1rem'>
          You own {formatWeightAsGrams(getQuantityOwned())}.
        </Typography>
        <Typography color='common.black' fontSize='1rem'>
          Current value is ${getCurrentValue()}
        </Typography>
      </Stack>

      <Stack px={2}>
        {/* TODO: make this a component */}
        <GridValue
          data={productPerformanceData}
          currentValue={getCurrentValue()}
        />
      </Stack>

      <Stack px={2} mt={2}>
        <Typography color='common.black' fontSize='0.75rem'>
          All amounts shown in NZD
        </Typography>
      </Stack>

      <Stack height={200} pl={2} mb={4} ml='-1rem' position='relative'>
        {isShowPerformanceGraph() ? (
          <PerformanceGraph
            setToast={setToast}
            user={user}
            graphPeriod={graphPeriod}
            productId={Number(product?.id)}
            vaultId={vaultId}
          />
        ) : (
          <NoPerformance productName={product?.name} />
        )}
      </Stack>

      <Stack>
        <GraphPeriod
          setGraphPeriod={setGraphPeriod}
          graphPeriod={graphPeriod}
        />
      </Stack>

      <Stack
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        px={2}
        mt={2}
      >
        <Typography variant='body2' color='primary'>
          ${getMidpointPerUnit()} PER{' '}
          {(product?.unitType ?? 'mg').toUpperCase()}
        </Typography>
        <Typography variant='body2' color='primary'>
          LIVE AT {formattedHours}:{formattedMinutes} {meridiem} {formattedDate}
        </Typography>
      </Stack>

      <Stack px={2} py={3} gap={1}>
        <Button variant='outlined' fullWidth onClick={handleClickSell}>
          Sell
        </Button>

        <Button variant='outlined' fullWidth onClick={handleClickBuyMore}>
          Buy More
        </Button>
      </Stack>

      <Stack px={2} py={3} gap={1}>
        <Typography color='primary' fontSize='1rem'>
          TRANSACTION HISTORY
        </Typography>
        <TableTransactionHistory productId={String(productId)} />
      </Stack>
    </Box>
  )
}

export default memo(MyVaultProduct)
